import { cn } from '@utils/cn';
import React from 'react';
import { useTranslation } from '@hooks/useTranslation';

export type TransactionType =
  | 'RECEIVE'
  | 'CONSUME'
  | 'ADJUST'
  | 'RESERVE'
  | 'RELEASE_RESERVE'
  | 'RETURN'
  | 'RECOVERY'
  | 'TRANSFER'
  | 'USE_AS_EQUIPMENT';

interface TransactionTypeBadgeProps {
  type: TransactionType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const TransactionTypeBadge: React.FC<TransactionTypeBadgeProps> = ({
  type,
  size = 'md',
  className = '',
}) => {
  // Sprint 29, story-148. Colors/icons unchanged — text-only fix.
  const tEnumLabels = useTranslation('enumLabels');

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs h-5',
    md: 'px-3 py-1 text-sm h-6',
    lg: 'px-4 py-1.5 text-base h-8',
  };

  const typeConfig: Record<
    TransactionType,
    { label: string; classes: string; icon: string }
  > = {
    RECEIVE: {
      label: tEnumLabels.transactionTypeReceive,
      classes: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100',
      icon: '↓',
    },
    CONSUME: {
      label: tEnumLabels.transactionTypeConsume,
      classes: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100',
      icon: '→',
    },
    ADJUST: {
      label: tEnumLabels.transactionTypeAdjust,
      classes: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100',
      icon: '±',
    },
    RESERVE: {
      label: tEnumLabels.transactionTypeReserve,
      classes: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100',
      icon: '⏸',
    },
    RELEASE_RESERVE: {
      label: tEnumLabels.transactionTypeReleaseReserve,
      classes: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100',
      icon: '▶',
    },
    RETURN: {
      label: tEnumLabels.transactionTypeReturn,
      classes: 'bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-100',
      icon: '↩',
    },
    RECOVERY: {
      label: tEnumLabels.transactionTypeRecovery,
      classes: 'bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-100',
      icon: '♻',
    },
    TRANSFER: {
      label: tEnumLabels.transactionTypeTransfer,
      classes: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-100',
      icon: '⇄',
    },
    USE_AS_EQUIPMENT: {
      label: tEnumLabels.transactionTypeUseAsEquipment,
      classes: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-100',
      icon: '🔧',
    },
  };

  const config = typeConfig[type];
  const baseClasses =
    'inline-flex items-center justify-center gap-1.5 rounded-full font-medium whitespace-nowrap w-fit';

  return (
    <span className={cn(baseClasses, sizeClasses[size], config.classes, className)}>
      <span className="text-xs">{config.icon}</span>
      {config.label}
    </span>
  );
};
